import { useTranslations } from '@vocab/react';
import {
  Box,
  IconArrow,
  Stack,
  Text,
  TextLink,
  Button,
} from 'braid-design-system';
import { useState, useEffect } from 'react';

import ShareButton from 'src/components/atoms/Buttons/ShareButton/ShareButton';
import ThreadSkeleton from 'src/components/atoms/Skeleton/ThreadSkeleton';
import ThreadDetailsCard from 'src/components/molecules/CommunityThreadDetails/ThreadDetailsCards/ThreadDetailsCard';
import ThreadDetailsCommentCard from 'src/components/molecules/CommunityThreadDetails/ThreadDetailsCommentCard/ThreadDetailsCommentCard';
import { PAGE_SIZE } from 'src/core/domain/constants';
import { type PageName, getPageName } from 'src/core/utils/pageUtil';
import { ErrorBoundary } from 'src/modules/errorBoundary/ErrorBoundary';
import { NAV_PATHS } from 'src/paths';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useConfig } from 'src/provider/config';
import { OptionDialogProvider } from 'src/provider/optionDialog';
import { usePage } from 'src/provider/page';
import { useProfile } from 'src/provider/profile';
import { RepliesProvider } from 'src/provider/replies';
import { useThreadDetail } from 'src/provider/threadDetail';
import {
  fetchComments,
  type fetchCommentsParams,
} from 'src/services/threadsService';

import translations from './.vocab';

import * as styles from './ThreadDetails.css';

const getCurrentAndPreviousHostNameAndPaths = (): {
  currentHostName: string;
  previousHostName: string;
  previousPageName: PageName;
} => {
  const isServer = typeof window === 'undefined';
  if (!isServer && window?.location?.href && window?.document?.referrer) {
    const { hostname: currentHostName } = new URL(window?.location?.href);
    const { hostname: previousHostName, pathname: previousPathName } = new URL(
      window?.document?.referrer,
    );
    const pageName = getPageName(previousPathName);
    return {
      currentHostName,
      previousHostName,
      previousPageName: pageName,
    };
  }

  return {
    currentHostName: '',
    previousHostName: '',
    previousPageName: 'unknown',
  };
};

// TODO: Update here if you have a new case for back navigation
const usePreviousUrl = () => {
  // Always return a consistent value during initial render for SSR
  const [previousUrl, setPreviousUrl] = useState(NAV_PATHS.communityHome);

  useEffect(() => {
    const { currentHostName, previousHostName, previousPageName } =
      getCurrentAndPreviousHostNameAndPaths();

    if (
      currentHostName === previousHostName &&
      (previousPageName === 'CommunityGroupDetails' ||
        previousPageName === 'CommunityHome')
    ) {
      setPreviousUrl(window?.document?.referrer || NAV_PATHS.communityHome);
    }
  }, []);

  return previousUrl;
};

const useGetBackButtonText = () => {
  const { t } = useTranslations(translations);
  const {
    threadDetailState: { thread },
  } = useThreadDetail();
  const defaultCategoryGroup = t('featuredThreads');

  const [backButtonText, setBackButtonText] = useState(defaultCategoryGroup);

  useEffect(() => {
    const { currentHostName, previousHostName, previousPageName } =
      getCurrentAndPreviousHostNameAndPaths();

    if (
      currentHostName === previousHostName &&
      previousPageName === 'CommunityGroupDetails' &&
      thread?.category?.name
    ) {
      setBackButtonText(thread.category.name);
    }
  }, [thread?.category?.name]);

  return backButtonText;
};

export default () => {
  const { t } = useTranslations(translations);
  const { pageDispatch } = usePage();
  const config = useConfig();
  const {
    threadDetailState: { thread, comments, hasNextPage, isLoadingComments },
    threadDetailDispatch,
  } = useThreadDetail();
  const { isAuthenticated } = useAuthStatus();
  const categoryLabel = useGetBackButtonText();
  const {
    state: { author },
  } = useProfile();

  if (!thread) return <ErrorBoundary />;

  const previousUrl = usePreviousUrl();
  const totalCommentsLoaded = comments.length;

  // Analytics
  const { trackAnalyticsInteraction } = useAnalytics();
  const handleShareAnalytics = () => {
    trackAnalyticsInteraction('ShareThreadClick', {
      threadId: thread.id,
      linkAction: 'share',
      linkPosition: 'thread',
    });
  };
  const handleFetchMoreComments = async () => {
    threadDetailDispatch({ type: 'LOADING_COMMENT' });
    const params: fetchCommentsParams = {
      threadId: thread.id,
      pageSize: PAGE_SIZE,
      isAuthenticated,
      cursor: comments.length,
      config,
    };
    const newComments = await fetchComments(params);
    threadDetailDispatch({
      type: 'ADD_MORE_COMMENT',
      payload: { comments: newComments },
    });
    threadDetailDispatch({ type: 'COMPLETE_LOADING_COMMENT' });
    trackAnalyticsInteraction('MoreThreadComment', {
      threadId: thread.id,
      totalCommentsLoaded,
    });
  };

  return (
    <Stack space="xxsmall">
      <Box
        display="flex"
        justifyContent="spaceBetween"
        className={styles.noTextDecorationSpan}
        style={{ paddingTop: '11px' }}
      >
        <Text tone="formAccent" data={{ testid: 'back-button' }}>
          <TextLink
            href={previousUrl}
            icon={<IconArrow direction="left" />}
            onClick={() => {
              pageDispatch({ type: 'IS_LOADING' });
            }}
          >
            {categoryLabel}
          </TextLink>
        </Text>
        <ShareButton
          shareLink={config.absoluteUrl}
          onShare={handleShareAnalytics}
        />
      </Box>
      <OptionDialogProvider
        value={{
          interaction: 'thread',
          id: thread.id,
          isAuthor: thread.author.id === author.id,
        }}
      >
        <ThreadDetailsCard threadDetails={thread} />
      </OptionDialogProvider>
      {comments?.map((comment) => (
        <OptionDialogProvider
          value={{
            interaction: 'comment',
            id: comment.id,
            isAuthor: comment.author.id === author.id,
          }}
          key={comment.id}
        >
          <RepliesProvider key={comment.id}>
            <ThreadDetailsCommentCard
              threadId={thread.id}
              commentDetails={comment}
            />
          </RepliesProvider>
        </OptionDialogProvider>
      ))}
      {isLoadingComments && <ThreadSkeleton itemsToDisplay={10} />}
      {hasNextPage && (
        <Box
          className={styles.ThreadDetailsButton}
          padding="small"
          justifyContent="center"
        >
          <Button
            onClick={handleFetchMoreComments}
            variant="ghost"
            tone="formAccent"
            size="small"
            loading={isLoadingComments}
          >
            {t('showMoreComments')}
          </Button>
        </Box>
      )}
    </Stack>
  );
};
